import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ragService, type RAGCostEstimate } from '../services/ragService';

interface Props {
  isOpen: boolean;
  kbId: string | null | undefined;
  onClose: () => void;
  onConfirm: () => void;
  forceReindex?: boolean;
  title?: string;
  confirmLabel?: string;
}

const RAGPrepareCostDialog: React.FC<Props> = ({ isOpen, kbId, onClose, onConfirm, forceReindex = false, title, confirmLabel }) => {
  const { t } = useTranslation();
  const [estimate, setEstimate] = useState<RAGCostEstimate | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !kbId) return;
    setEstimate(null);
    setError(null);
    setLoading(true);

    ragService.estimate(kbId, { forceReindex })
      .then(est => setEstimate(est))
      .catch(e => setError(e?.response?.data?.detail || e?.message || 'Failed to estimate cost'))
      .finally(() => setLoading(false));
  }, [isOpen, kbId]);

  const formatCost = (cents: number, currency: string) => {
    const amount = cents / 100;
    return `${currency === 'EUR' ? '€' : '$'}${amount.toFixed(2)}`;
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold">{title ?? t('ragPrepare.costTitle', 'RAG Preparation — Cost Estimate')}</h2>
          <span className="text-slate-400 hover:text-slate-600 text-xl cursor-pointer" onClick={onClose}>&times;</span>
        </div>

        <div className="px-5 py-4 space-y-4">

          {loading && (
            <p className="text-sm text-slate-500 text-center py-4">{t('ragPrepare.estimating', 'Estimating cost...')}</p>
          )}

          {error && (
            <p className="text-sm text-red-600 bg-red-50 rounded px-3 py-2">{error}</p>
          )}

          {estimate && !loading && (
            <div className="bg-slate-50 rounded p-3 space-y-1.5 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600">{t('ragPrepare.totalDocs', 'Total documents')}</span>
                <span className="font-medium">{estimate.total_documents}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">{t('ragPrepare.docsToProcess', 'Documents to process')}</span>
                <span className="font-medium">{estimate.documents_to_process}</span>
              </div>
              {estimate.skipped_documents > 0 && (
                <>
                  <div className="flex justify-between text-amber-700">
                    <span>{t('ragPrepare.docsSkipped', 'Previously prepared, unchanged')}</span>
                    <span className="font-medium">{estimate.skipped_documents}</span>
                  </div>
                  <p className="text-xs text-slate-400 -mt-1">{t('ragPrepare.docsSkippedHint', 'Already chunked and indexed from a previous preparation run.')}</p>
                </>
              )}
              {estimate.not_preparable_documents > 0 && (
                <div className="flex justify-between text-orange-600">
                  <span>{t('ragPrepare.docsNotPreparable', 'Not preparable (no text content)')}</span>
                  <span className="font-medium">{estimate.not_preparable_documents}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-slate-600">{t('ragPrepare.estimatedChunks', 'Estimated chunks')}</span>
                <span className="font-medium">{estimate.estimated_chunks.toLocaleString()}</span>
              </div>
              <div className="border-t border-slate-200 pt-1.5 mt-1.5 flex justify-between">
                <span className="text-slate-700 font-medium">{t('ragPrepare.estimatedCost', 'Estimated cost')}</span>
                <span className="font-bold text-base">{formatCost(estimate.estimated_cost_cents, estimate.currency)}</span>
              </div>
              <div className="flex justify-between text-slate-500 text-xs">
                <span>{t('ragPrepare.embeddingModel', 'Embedding model')}</span>
                <span>{estimate.model_name || estimate.model_id}</span>
              </div>
              {estimate.pricing_warning && (
                <p className="text-xs text-amber-600 bg-amber-50 rounded px-2 py-1 mt-1">
                  {estimate.pricing_warning}
                </p>
              )}
              <p className="text-xs text-slate-400 pt-1">{t('ragPrepare.disclaimer', 'Cost is approximate and depends on actual document content.')}</p>
            </div>
          )}

          {estimate && estimate.documents_to_process === 0 && !loading && (
            <p className="text-sm text-slate-500 italic">{t('ragPrepare.nothingToProcess', 'All documents are already indexed. No cost expected.')}</p>
          )}

        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-slate-600 border border-slate-300 rounded hover:bg-slate-50"
          >
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            onClick={() => { onConfirm(); onClose(); }}
            disabled={loading || !!error}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {confirmLabel ?? t('ragPrepare.confirmLaunch', 'Prepare RAG')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RAGPrepareCostDialog;
